import coreSkillsApi from './coreskillsApi';
import { LONG_TIMEOUT_MS } from 'config/constants';

export const CS_API = Object.freeze({
  ocr: {
    upload: '/api/ocr/process',
    list: '/api/ocr/documents',
    detail: (id: string) => `/api/ocr/documents/${id}`,
  },
  audiobooks: {
    list: '/api/audiobooks/',
    fromOcr: (docId: string) => `/api/audiobooks/from-ocr/${docId}`,
    details: (id: string) => `/api/audiobooks/${id}`,
    extractToc: (id: string) => `/api/audiobooks/${id}/extract-toc`,
    createChapters: (id: string) => `/api/audiobooks/${id}/create-chapters`,
    chapters: (id: string) => `/api/audiobooks/${id}/chapters`,
    chapterContent: (id: string, chapterId: string) => `/api/audiobooks/${id}/chapters/${chapterId}/content`,
    updateChapterContent: (id: string, chapterId: string) => `/api/audiobooks/${id}/chapters/${chapterId}/content`,
    processChapter: (id: string, chapterId: string) => `/api/audiobooks/${id}/chapters/${chapterId}/process`,
    approveChapter: (id: string, chapterId: string) => `/api/audiobooks/${id}/chapters/${chapterId}/approve`,
    generateChapterAudio: (id: string, chapterId: string) =>
      `/api/audiobooks/${id}/chapters/${chapterId}/generate-audio`,
    processAll: (id: string) => `/api/audiobooks/${id}/process-all`,
    processFull: (id: string) => `/api/audiobooks/${id}/process-full`,
    audioUrls: (id: string) => `/api/audiobooks/${id}/audio-urls`,
    runs: (id: string) => `/api/audiobooks/${id}/runs`,
    run: (id: string, runId: string) => `/api/audiobooks/${id}/runs/${runId}`,
    linkPublic: (id: string) => `/api/audiobooks/${id}/link-public`,
    voices: {
      elevenlabs: '/api/audiobooks/voices/elevenlabs',
      openai: '/api/audiobooks/voices/openai',
    },
  },
});

export function uploadOcrPdf(file: File, params: { document_type: 'audiobook' | 'course'; organization_id?: string }) {
  const formData = new FormData();
  formData.append('file', file);
  return coreSkillsApi.post(CS_API.ocr.upload, formData, { params, timeout: LONG_TIMEOUT_MS });
}

export function listAudiobooks(params?: { limit?: number; start_after?: string; organization_id?: string }) {
  return coreSkillsApi.get(CS_API.audiobooks.list, { params });
}

// Lightweight list for sidebar and fast lists
export function listAudiobooksLight(params?: { limit?: number; start_after?: string; organization_id?: string }) {
  return coreSkillsApi.get('/api/audiobooks/ajassa/list', {
    params,
    headers: { Accept: 'application/json' },
  });
}
export function listOcrDocuments(
  params: {
    document_type?: string;
    organization_id?: string;
    limit?: number;
    cursor_updated_at?: string;
    cursor_id?: string;
  },
  headers?: Record<string, string>
) {
  return coreSkillsApi.get(CS_API.ocr.list, { params, headers });
}

export function deleteOcrDocument(id: string) {
  return coreSkillsApi.delete(CS_API.ocr.detail(id));
}

export function createAudiobookFromOcr(
  ocrDocumentId: string,
  body: { language: string; title?: string; author?: string; organization_id?: string }
) {
  return coreSkillsApi.post(CS_API.audiobooks.fromOcr(ocrDocumentId), body);
}

export function getAudiobookDetails(id: string) {
  return coreSkillsApi.get(CS_API.audiobooks.details(id));
}

export function triggerTocExtraction(id: string) {
  return coreSkillsApi.post(CS_API.audiobooks.extractToc(id), {});
}

export function saveTocAndCreateChapters(
  id: string,
  body: { page_offset: number; chapters: Array<Record<string, unknown>> }
) {
  return coreSkillsApi.post(CS_API.audiobooks.createChapters(id), body, { timeout: LONG_TIMEOUT_MS });
}

export function listChapters(id: string) {
  return coreSkillsApi.get(CS_API.audiobooks.chapters(id));
}

export function getChapterContent(id: string, chapterId: string) {
  return coreSkillsApi.get(CS_API.audiobooks.chapterContent(id, chapterId));
}

export function putChapterContent(id: string, chapterId: string, body: { content: string; update_raw?: boolean }) {
  return coreSkillsApi.put(CS_API.audiobooks.updateChapterContent(id, chapterId), body, {
    headers: { 'Content-Type': 'application/json' },
  });
}

export function processChapter(id: string, chapterId: string) {
  return coreSkillsApi.post(CS_API.audiobooks.processChapter(id, chapterId), {});
}

export function approveChapter(id: string, chapterId: string, voice_id?: string) {
  return coreSkillsApi.post(CS_API.audiobooks.approveChapter(id, chapterId), undefined, {
    params: voice_id ? { voice_id } : undefined,
  });
}

export function generateChapterAudio(
  id: string,
  chapterId: string,
  body: { use_openai?: boolean; voice_id?: string; stability?: number; similarity_boost?: number }
) {
  return coreSkillsApi.post(CS_API.audiobooks.generateChapterAudio(id, chapterId), body, { timeout: LONG_TIMEOUT_MS });
}

export function generateAudioBatch(
  id: string,
  body: {
    use_openai?: boolean;
    voice_id?: string;
    stability?: number;
    similarity_boost?: number;
    chapter_ids?: string[];
  }
) {
  return coreSkillsApi.post(`/api/audiobooks/${id}/generate-audio-batch`, body, { timeout: LONG_TIMEOUT_MS });
}

export function processAll(id: string, force?: boolean) {
  const url = force ? `${CS_API.audiobooks.processAll(id)}?force=true` : CS_API.audiobooks.processAll(id);
  return coreSkillsApi.post(url, {}, { timeout: LONG_TIMEOUT_MS });
}

export function processFull(id: string) {
  return coreSkillsApi.post(CS_API.audiobooks.processFull(id), {}, { timeout: LONG_TIMEOUT_MS });
}

export function listAudioUrls(id: string) {
  return coreSkillsApi.get(CS_API.audiobooks.audioUrls(id));
}

// Progress runs
export function listRuns(
  id: string,
  params?: { type?: 'toc_extraction' | 'chapter_creation' | 'text_cleaning' | 'audio_generation' }
) {
  return coreSkillsApi.get(CS_API.audiobooks.runs(id), { params, timeout: 15000 });
}

export function getRun(
  id: string,
  runId: string
): Promise<{
  data: {
    id: string;
    type: string;
    audiobookId: string;
    status: 'running' | 'completed' | 'failed' | 'cancelled';
    startedAt?: string;
    finishedAt?: string;
    totalChapters?: number;
    completedChapters?: number;
    failedChapters?: number;
    lastMessage?: string;
    lastUpdateAt?: string;
    chapters?: Array<{
      chapterId: string;
      title?: string;
      order?: number;
      status?: 'running' | 'done' | 'failed';
      processedUnits?: number;
      totalUnits?: number;
      stage?: string;
      lastMessage?: string;
      updatedAt?: string;
    }>;
  };
}> {
  return coreSkillsApi.get(CS_API.audiobooks.run(id, runId), { timeout: 15000 });
}

// Cancel a single run
export function cancelRun(id: string, runId: string) {
  return coreSkillsApi.post(`/api/audiobooks/${id}/runs/${runId}/cancel`, {});
}

// Cancel all running runs of a given type (defaults to audio_generation)
export function cancelAllRuns(
  id: string,
  params?: { type?: 'audio_generation' | 'text_cleaning' | 'chapter_creation' | 'toc_extraction' }
) {
  return coreSkillsApi.post(`/api/audiobooks/${id}/runs/cancel-all`, {}, { params });
}

// Reset chapter audio status to a generateable state (e.g., approved)
export function resetChapterAudio(
  id: string,
  chapterId: string,
  body?: { target_status?: 'approved'; force?: boolean }
) {
  return coreSkillsApi.post(`/api/audiobooks/${id}/chapters/${chapterId}/audio/reset`, body ?? {});
}

export function listVoicesElevenLabs() {
  return coreSkillsApi.get(CS_API.audiobooks.voices.elevenlabs);
}

export function getVoicePreview(provider: 'elevenlabs' | 'openai', voiceId: string) {
  const base = provider === 'openai' ? CS_API.audiobooks.voices.openai : CS_API.audiobooks.voices.elevenlabs;
  return coreSkillsApi.get(`${base}/${voiceId}/preview`, {
    responseType: 'blob',
    timeout: LONG_TIMEOUT_MS,
  });
}

export function listVoicesOpenAI() {
  return coreSkillsApi.get(CS_API.audiobooks.voices.openai);
}

// Admin status toggle
export function setAudiobookStatus(id: string, status: 'completed' | 'chapters_created') {
  return coreSkillsApi.patch(`/api/audiobooks/${id}/status`, { status }, { headers: { Accept: 'application/json' } });
}

// Delete OCR-based audiobook
export function deleteAudiobook(id: string) {
  return coreSkillsApi.delete(CS_API.audiobooks.details(id));
}

export function linkCsAudiobookToPublic(id: string, publicAudiobookId: string) {
  return coreSkillsApi.post(CS_API.audiobooks.linkPublic(id), { publicAudiobookId });
}
